import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { SortTool } from '../../common/react/components/SortTool';
import { OrderKeys } from './OrderKeys';

export namespace ContentSortOptions {

  export type SortOptionDictionary = {readonly [key: string]: Readonly<SortTool.SortOption>};

  export function sortOptionsForValidKeysFromList(
      keysOrFalses: string[],
      options: SortOptionDictionary,
  ): SortTool.SortOption[] {
    return _.map(_.compact(keysOrFalses), key => options[key]);
  }

  export function sortOptionsForValidKeys(keysOrFalses: string[]): SortTool.SortOption[] {
    return sortOptionsForValidKeysFromList(keysOrFalses, SortBy);
  }

  export function supportsAscAndDesc(sortKey: string): boolean {
    return sortKey !== OrderKeys.contentTypeOrder && sortKey !== OrderKeys.targetType;
  }

  // this is for the sort dropdown-- some of these don't have asc and desc defined here because
  // they don't show up in the dropdown, but they might still support asc and desc.
  export const SortBy: SortOptionDictionary = {
    [OrderKeys.contentTypeOrder]: {
      field: OrderKeys.contentTypeOrder,
      locFn: Messages.column_content_type,
      direction: 'asc'
    },
    [OrderKeys.connectionTypeDisplayName]: {
      field: OrderKeys.connectionTypeDisplayName,
      locFn: Messages.connectionType,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByAZ,
          selectButtonLocFn: Messages.sortByConnectionTypeAsc
        },
        desc: {
          locFn: Messages.sortByZA,
          selectButtonLocFn: Messages.sortByConnectionTypeDesc
        }
      }
    },
    [OrderKeys.connectedWorkbooksCount]: {
      field: OrderKeys.connectedWorkbooksCount,
      locFn: Messages.workbooks,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByNumWorkbooksAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByNumWorkbooksDesc
        }
      }
    },
    'datasourceName': {
      field: OrderKeys.name,
      locFn: Messages.datasource,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByAZ,
          selectButtonLocFn: Messages.sortByDataSourceAsc
        },
        desc: {
          locFn: Messages.sortByZA,
          selectButtonLocFn: Messages.sortByDataSourceDesc
        }
      },
    },
    [OrderKeys.createdAt]: {
      field: OrderKeys.createdAt,
      locFn: Messages.created,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByOldNew,
          selectButtonLocFn: Messages.sortByCreatedAsc
        },
        desc: {
          locFn: Messages.sortByNewOld,
          selectButtonLocFn: Messages.sortByCreatedDesc
        }
      }
    },
    [OrderKeys.datasourceCount]: {
      field: OrderKeys.datasourceCount,
      locFn: Messages.datasources,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByNumDatasourcesAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByNumDatasourcesDesc
        }
      }
    },
    [OrderKeys.favoritesTotal]: {
      field: OrderKeys.favoritesTotal,
      locFn: Messages.favoritesTotal,
      direction: 'desc'
    },
    [OrderKeys.hitsLastOneMonthTotal]: {
      field: OrderKeys.hitsLastOneMonthTotal,
      locFn: Messages.hitsLastOneMonthTotal,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByHitsLastOneMonthTotalAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByHitsLastOneMonthTotalDesc
        }
      }
    },
    [OrderKeys.hasAlert]: {
      field: OrderKeys.hasAlert,
      locFn: Messages.alert,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.hasAlert,
          selectButtonLocFn: Messages.hasAlert
        },
        desc: {
          locFn: Messages.sortByHasNoAlert,
          selectButtonLocFn: Messages.sortByHasNoAlert
        }
      }
    },
    [OrderKeys.hitsLastThreeMonthsTotal]: {
      field: OrderKeys.hitsLastThreeMonthsTotal,
      locFn: Messages.hitsLastThreeMonthsTotal,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByHitsLastThreeMonthsTotalAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByHitsLastThreeMonthsTotalDesc
        }
      }
    },
    [OrderKeys.hitsLastTwelveMonthsTotal]: {
      field: OrderKeys.hitsLastTwelveMonthsTotal,
      locFn: Messages.hitsLastTwelveMonthsTotal,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByHitsLastTwelveMonthsTotalAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByHitsLastTwelveMonthsTotalDesc
        }
      }
    },
    [OrderKeys.hitsTotal]: {
      field: OrderKeys.hitsTotal,
      locFn: Messages.hitsTotal,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByHitsTotalAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByHitsTotalDesc
        }
      }
    },
    [OrderKeys.index]: {
      field: OrderKeys.index,
      locFn: Messages.sheet,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByFirstLast,
          selectButtonLocFn: Messages.sortBySheetNumberAsc
        },
        desc: {
          locFn: Messages.sortByLastFirst,
          selectButtonLocFn: Messages.sortBySheetNumberDesc
        }
      }
    },
    [OrderKeys.lastRefreshedAt]: {
      field: OrderKeys.lastRefreshedAt,
      locFn: Messages.lastRefreshedAt,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByOldNew,
          selectButtonLocFn: Messages.sortByLastRefreshedAtAsc
        },
        desc: {
          locFn: Messages.sortByNewOld,
          selectButtonLocFn: Messages.sortByLastRefreshedAtDesc
        }
      }
    },
    [OrderKeys.name]: {
      field: OrderKeys.name,
      locFn: Messages.name,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByAZ,
          selectButtonLocFn: Messages.sortByNameAsc
        },
        desc: {
          locFn: Messages.sortByZA,
          selectButtonLocFn: Messages.sortByNameDesc
        }
      },
    },
    [OrderKeys.numSheets]: {
      field: OrderKeys.numSheets,
      locFn: Messages.sheets,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByNumSheetsAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByNumSheetsDesc
        }
      }
    },
    [OrderKeys.ownerName]: {
      field: OrderKeys.ownerName,
      locFn: Messages.owner,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByAZ,
          selectButtonLocFn: Messages.sortByOwnerAsc
        },
        desc: {
          locFn: Messages.sortByZA,
          selectButtonLocFn: Messages.sortByOwnerDesc
        }
      }
    },
    [OrderKeys.projectName]: {
      field: OrderKeys.projectName,
      locFn: Messages.project,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByAZ,
          selectButtonLocFn: Messages.sortByProjectAsc
        },
        desc: {
          locFn: Messages.sortByZA,
          selectButtonLocFn: Messages.sortByProjectDesc
        }
      }
    },
    [OrderKeys.relevance]: {
      field: OrderKeys.relevance,
      locFn: Messages.relevance,
      direction: 'desc'
    },
    [OrderKeys.status]: {
      field: OrderKeys.status,
      locFn: () => '',
      direction: 'desc',
    },
    [OrderKeys.jobId]: {
      field: OrderKeys.jobId,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.taskType]: {
      field: OrderKeys.taskType,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.currentRunTime]: {
      field: OrderKeys.currentRunTime,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.currentQueueTime]: {
      field: OrderKeys.currentQueueTime,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.jobRequestedTime]: {
      field: OrderKeys.jobRequestedTime,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.flowOutputStepName]: {
      field: OrderKeys.flowOutputStepName,
      locFn: ()=> '',
      direction: 'asc',
    },
    [OrderKeys.startedAt]: {
      field: OrderKeys.startedAt,
      locFn: () => '',
      direction: 'desc',
    },
    [OrderKeys.completedAt]: {
      field: OrderKeys.completedAt,
      locFn: () => '',
      direction: 'desc',
    },
    [OrderKeys.rowsProduced]: {
      field: OrderKeys.rowsProduced,
      locFn: () => '',
      direction: 'desc',
    },
    [OrderKeys.size]: {
      field: OrderKeys.size,
      locFn: Messages.size,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortBySmallLarge,
          selectButtonLocFn: Messages.sortBySizeAsc
        },
        desc: {
          locFn: Messages.sortByLargeSmall,
          selectButtonLocFn: Messages.sortBySizeDesc
        }
      }
    },
    [OrderKeys.updatedAt]: {
      field: OrderKeys.updatedAt,
      locFn: Messages.modified,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByOldNew,
          selectButtonLocFn: Messages.sortByUpdatedAsc
        },
        desc: {
          locFn: Messages.sortByNewOld,
          selectButtonLocFn: Messages.sortByUpdatedDesc
        }
      }
    },
    [OrderKeys.viewCount]: {
      field: OrderKeys.viewCount,
      locFn: Messages.views,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByNumViewsAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByNumViewsDesc
        }
      }
    },
    [OrderKeys.workbookCount]: {
      field: OrderKeys.workbookCount,
      locFn: Messages.workbooks,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByNumWorkbooksAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByNumWorkbooksDesc
        }
      }
    },
    [OrderKeys.workbookName]: {
      field: OrderKeys.workbookName,
      locFn: Messages.workbook,
      direction: 'asc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByAZ,
          selectButtonLocFn: Messages.sortByWorkbookAsc
        },
        desc: {
          locFn: Messages.sortByZA,
          selectButtonLocFn: Messages.sortByWorkbookDesc
        }
      }
    },
    [OrderKeys.childProjectCount]: {
      field: OrderKeys.childProjectCount,
      locFn: Messages.projects,
      direction: 'desc',
      directionOptions: {
        asc: {
          locFn: Messages.sortByLeastMost,
          selectButtonLocFn: Messages.sortByNumChildrenAsc
        },
        desc: {
          locFn: Messages.sortByMostLeast,
          selectButtonLocFn: Messages.sortByNumChildrenDesc
        }
      }
    },
    [OrderKeys.scheduleName]: {
      field: OrderKeys.scheduleName,
      locFn: Messages.schedule,
      direction: 'asc'
    },
    [OrderKeys.subject]: {
      field: OrderKeys.subject,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.userDisplayName]: {
      field: OrderKeys.userDisplayName,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.targetName]: {
      field: OrderKeys.targetName,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.creatorName]: {
      field: OrderKeys.creatorName,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.lastRunAt]: {
      field: OrderKeys.lastRunAt,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.runNextAt]: {
      field: OrderKeys.runNextAt,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.type]: {
      field: OrderKeys.type,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.priority]: {
      field: OrderKeys.priority,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.lastSentAt]: {
      field: OrderKeys.lastSentAt,
      locFn: () => '',
      direction: 'desc',
    },
    [OrderKeys.refreshType]: {
      field: OrderKeys.refreshType,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.definition]: {
      field: OrderKeys.definition,
      locFn: () => '',
      direction: 'asc',
    },
    [OrderKeys.lastAccessedAt]: {
      field: OrderKeys.lastAccessedAt,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.metricValue]: {
      field: OrderKeys.metricValue,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.metricUpdated]: {
      field: OrderKeys.metricUpdated,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.connectedViewName]: {
      field: OrderKeys.connectedViewName,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.lastChecked]: {
      field: OrderKeys.lastChecked,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.notificationIntervalInMinutes]: {
      field: OrderKeys.notificationIntervalInMinutes,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.title]: {
      field: OrderKeys.title,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.adminCount]: {
      field: OrderKeys.adminCount,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.authoringEnabled]: {
      field: OrderKeys.authoringEnabled,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.availability]: {
      field: OrderKeys.availability,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.currentLoggedInUserCount]: {
      field: OrderKeys.currentLoggedInUserCount,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.extractEncryptionMode]: {
      field: OrderKeys.extractEncryptionMode,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.guestAccessEnabled]: {
      field: OrderKeys.guestAccessEnabled,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.sheetImageEnabled]: {
      field: OrderKeys.sheetImageEnabled,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.metricsEnabled]: {
      field: OrderKeys.metricsEnabled,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.siteAdminsCanAddUsers]: {
      field: OrderKeys.siteAdminsCanAddUsers,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.storageUsedBytes]: {
      field: OrderKeys.storageUsedBytes,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.storageQuotaBytes]: {
      field: OrderKeys.storageQuotaBytes,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.storageQuotaPercentUsed]: {
      field: OrderKeys.storageQuotaPercentUsed,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.urlName]: {
      field: OrderKeys.urlName,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.userCount]: {
      field: OrderKeys.userCount,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.userQuota]: {
      field: OrderKeys.userQuota,
      locFn: () => '',
      direction: 'asc'
    },
    [OrderKeys.lastSignIn]: {
      field: OrderKeys.lastSignIn,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.metricsEnabled]: {
      field: OrderKeys.metricsEnabled,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.authoringEnabled]: {
      field: OrderKeys.authoringEnabled,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.sheetImageEnabled]: {
      field: OrderKeys.sheetImageEnabled,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.siteAdminsCanAddUsers]: {
      field: OrderKeys.siteAdminsCanAddUsers,
      locFn: () => '',
      direction: 'desc'
    },
    [OrderKeys.extractEncryptionMode]: {
      field: OrderKeys.extractEncryptionMode,
      locFn: () => '',
      direction: 'desc'
    },
  };
}
